import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Pencil, X, Check } from 'lucide-react';
import Layout from '../../components/layout/layout';
import Button from '../../components/ui/button';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import HeroTemplateCandidats from '../../components/sections/HeroTemplateCandidats';

interface Tool {
  id: string;
  name: string;
  description: string;
  image_url: string | null;
  button_url: string;
}

export default function OutilsPage() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: userData } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();

      setIsAdmin(userData?.role === 'admin');
    };

    const fetchTools = async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching tools:', error);
        return;
      }

      const updatedData = data?.map(tool => {
        if (tool.name === 'PimpMyJob') {
          return {
            ...tool,
            image_url: 'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/offreemploiia.svg'
          };
        }
        if (tool.name === 'Mapping RH') {
          return {
            ...tool,
            image_url: 'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/MappingRH.svg'
          };
        }
        return tool;
      });

      setTools(updatedData || []);
      setIsLoading(false);
    };

    checkAdmin();
    fetchTools();
  }, []);

  const handleEdit = (tool: Tool) => {
    setEditingTool(tool);
  };

  const handleSave = async () => {
    if (!editingTool) return;

    const { error } = await supabase
      .from('tools')
      .update({
        name: editingTool.name,
        description: editingTool.description,
        image_url: editingTool.image_url,
        button_url: editingTool.button_url,
      })
      .eq('id', editingTool.id);

    if (error) {
      toast.error('Erreur lors de la mise à jour');
      return;
    }

    toast.success('Outil mis à jour');
    setEditingTool(null);

    const { data } = await supabase
      .from('tools')
      .select('*')
      .order('created_at', { ascending: true });

    setTools(data || []);
  };

  const handleImageUpload = async (file: File) => {
    if (!editingTool) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    const toastId = toast.loading('Upload en cours...');

    try {
      const filePath = `tools/${Date.now()}-${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('tools')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('tools')
        .getPublicUrl(filePath);

      setEditingTool({
        ...editingTool,
        image_url: publicUrl
      });

      toast.success('Image mise à jour', { id: toastId });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error("Erreur lors de l'upload", { id: toastId });
    }
  };

  return (
    <Layout>
      <WebPageSchema
        page={{
          title: "Outils RH innovants | recruteurs.io",
          description: "Découvrez notre suite d'outils RH conçus pour simplifier et optimiser vos processus de recrutement.",
          url: "https://recruteurs.io/ressources/outils"
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: "Accueil", url: "https://recruteurs.io" },
          { name: "Ressources", url: "https://recruteurs.io/ressources" },
          { name: "Outils", url: "https://recruteurs.io/ressources/outils" }
        ]}
      />

      <HeroTemplateCandidats
        page="outils"
        title={
          <>
            Nos outils RH{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-yellow-400">
              augmentés par l'IA
            </span>
          </>
        }
        description="Simplifiez votre recrutement avec notre suite d’outils conçus pour automatiser, accélérer et fiabiliser chaque étape de vos processus métier RH."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Conseil-IA-marseille.webp"
          imageAlt="Conseil RH assisté par IA à Marseille"

        cta1={{
          label: "📘 Voir les offres Recruteurs.io",
          href: "/offres"
        }}
        cta2={{
          label: "🔍 Demander mon audit IA gratuit",
          href: "/audit",
          variant: "outline"
        }}
      />

      <div className="py-20">
        <div className="container-custom">
          {isLoading ? (
            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-white rounded-xl p-6 shadow-lg">
                    <div className="flex gap-8">
                      <div className="w-1/3">
                        <div className="h-48 bg-neutral-200 rounded" />
                      </div>
                      <div className="w-2/3">
                        <div className="h-6 bg-neutral-200 rounded w-3/4 mb-4" />
                        <div className="h-4 bg-neutral-200 rounded w-full mb-2" />
                        <div className="h-4 bg-neutral-200 rounded w-2/3" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-8 max-w-5xl mx-auto">
              {tools.map((tool) => (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                >
                  {editingTool?.id === tool.id ? (
                    <div className="p-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Nom</label>
                          <input
                            type="text"
                            value={editingTool.name}
                            onChange={(e) => setEditingTool({ ...editingTool, name: e.target.value })}
                            className="w-full px-3 py-2 border rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Description</label>
                          <textarea
                            value={editingTool.description}
                            onChange={(e) => setEditingTool({ ...editingTool, description: e.target.value })}
                            className="w-full px-3 py-2 border rounded"
                            rows={3}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">URL du bouton</label>
                          <input
                            type="text"
                            value={editingTool.button_url}
                            onChange={(e) => setEditingTool({ ...editingTool, button_url: e.target.value })}
                            className="w-full px-3 py-2 border rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Image</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload(file);
                            }}
                            className="w-full"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setEditingTool(null)}>
                            <X size={18} />
                          </Button>
                          <Button variant="primary" onClick={handleSave}>
                            <Check size={18} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col md:flex-row">
                      {tool.image_url && (
                        <div className="md:w-1/3">
                          <img
                            src={tool.image_url}
                            alt={tool.name}
                            className="w-full h-full object-contain p-4"
                          />
                        </div>
                      )}
                      <div className="md:w-2/3 p-8">
                        <div className="flex items-start justify-between gap-4">
                          <h3 className="text-2xl font-bold">{tool.name}</h3>
                          {isAdmin && (
                            <button
                              onClick={() => handleEdit(tool)}
                              className="text-neutral-400 hover:text-primary transition-colors"
                            >
                              <Pencil size={18} />
                            </button>
                          )}
                        </div>
                        <p className="text-neutral-600 mt-4 mb-6">{tool.description}</p>
                        <Button
                          variant="gradient"
                          onClick={() => window.location.href = tool.button_url}
                        >
                          Découvrir {tool.name}
                        </Button>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-20 bg-[#30475e] text-neutral-200 rounded-2xl p-12">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4 text-neutral-100">
                🛠 Tous ces outils IA sont déjà inclus dans nos offres Recruteurs.io
              </h2>
              <p className="text-xl mb-6 text-neutral-300">
                Vous êtes client chez nous ?<br />
                Alors vous avez accès gratuitement à tous nos outils IA. Pas d'option cachée, pas d'upsell surprise.
              </p>
              <div className="bg-white/10 backdrop-blur rounded-xl p-8 mb-8">
                <p className="text-lg mb-2 text-neutral-200">
                  Vous sentez que vos recrutements mériteraient un petit coup de polish ?
                </p>
                <p className="text-2xl font-bold mb-2 text-neutral-100">
                  💡 Profitez d'un audit gratuit
                </p>
                <p className="text-neutral-300">
                  pour identifier comment l'IA peut pimper vos process RH et automatiser les étapes chronophages.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gradient" size="lg" onClick={() => window.location.href = '/offres'}>
                  📘 Voir les offres Recruteurs.io
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.href = '/audit'}
                  className="text-white border-white hover:bg-white/10"
                >
                  🔍 Demander mon audit IA gratuit
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}